import React, { useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { usePageContents } from '../contexts/PageContentContext';

const CurriculumInfo: React.FC = () => {
  const { getPageContent, loading } = usePageContents();
  const content = getPageContent('curriculum');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex items-center justify-center min-h-[400px] mt-20">
          <div className="text-gray-500">로딩 중...</div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      {/* Page Header */}
      <div className="relative text-white py-12 sm:py-16 md:py-20 lg:py-24 mt-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=1920&h=600&fit=crop&q=80"
            alt="Curriculum"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/50"></div>
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <p className="text-gray-200 mb-2 sm:mb-3 text-xs sm:text-sm font-medium tracking-wider">프로그램</p>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-3 sm:mb-4">교육과정</h1>
          <div className="w-16 sm:w-20 h-1 bg-white/70"></div>
        </div>
      </div>

      {/* Mission Section - White Background */}
      <div className="flex-1 pt-20 pb-10 bg-white">
        <div className="max-w-6xl mx-auto px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4 leading-relaxed">
              {content?.title_main || '글로벌 시대를 준비하는'}
            </h2>
            <h2 className="text-4xl md:text-5xl font-bold text-green-600 mb-8 leading-relaxed">
              {content?.title_sub || '체계적인 교육과정'}
            </h2>
            <p className="text-xl text-gray-500 mb-6">
              {content?.title_eng || 'Systematic Curriculum for the Global Era'}
            </p>
            <div className="w-20 h-1 bg-green-500 mx-auto"></div>
          </div>

          {/* 페이지 이미지가 있으면 표시 */}
          {content?.image_url && (
            <div className="mt-12">
              <img
                src={content.image_url}
                alt="교육과정 이미지"
                className="w-full max-w-4xl mx-auto rounded-2xl shadow-lg"
              />
            </div>
          )}
        </div>
      </div>

      {/* Semester Management Section - White Background */}
      <div className="pt-10 pb-20 bg-white">
        <div className="max-w-6xl mx-auto px-8">
          <div className="text-center mb-12">
            <div className="inline-block mb-4">
              <span className="text-3xl">✹</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">
              학기운영
            </h2>
            <p className="text-gray-600">Semester Management</p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
              <div className="p-8 md:p-10 space-y-6">
                {/* 학기운영 */}
                <div className="flex items-start gap-4 pb-6 border-b border-gray-100">
                  <div className="flex-shrink-0 w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-800 mb-3">학기운영</h3>
                    <div className="space-y-2">
                      <p className="text-gray-700">
                        <span className="font-semibold text-green-600">봄학기</span> : 2026년 3월 ~ 6월
                      </p>
                      <p className="text-gray-700">
                        <span className="font-semibold text-green-600">가을학기</span> : 2026년 9월 ~ 12월
                      </p>
                    </div>
                  </div>
                </div>

                {/* 운영기간 */}
                <div className="flex items-start gap-4 pb-6 border-b border-gray-100">
                  <div className="flex-shrink-0 w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-800 mb-3">운영기간</h3>
                    <p className="text-gray-700">
                      화 ~ 토요일 <span className="text-gray-500">(매주 월요일 휴관)</span>
                    </p>
                  </div>
                </div>

                {/* 운영시간 */}
                <div className="flex items-start gap-4 pb-6 border-b border-gray-100">
                  <div className="flex-shrink-0 w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-800 mb-3">운영시간</h3>
                    <p className="text-gray-700">09:00 ~ 18:00</p>
                  </div>
                </div>

                {/* 운영대상 */}
                <div className="flex items-start gap-4 pb-6 border-b border-gray-100">
                  <div className="flex-shrink-0 w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-800 mb-3">운영대상</h3>
                    <p className="text-gray-700">
                      관내 아동(6~7세), 초·중·고 학생, 성인
                    </p>
                  </div>
                </div>

                {/* 신청방법 */}
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-800 mb-3">신청방법</h3>
                    <p className="text-gray-700">
                      홈페이지를 통한 <span className="font-semibold text-green-600">개인 접수</span> 및 <span className="font-semibold text-green-600">체험학습 단체 접수</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Description Section - White Background */}
      {content?.description && content.description.trim() !== '' && (
        <div className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-8">
            <style>
              {`
                /* prose 클래스가 img를 block으로 만들어서 text-align이 안 먹힘 */
                /* 강제로 inline-block으로 변경하여 text-align이 작동하도록 함 */
                .curriculum-content p[style*="text-align"] img {
                  display: inline-block !important;
                  margin-left: auto !important;
                  margin-right: auto !important;
                  max-width: 100% !important;
                  height: auto !important;
                }

                /* 또는 flex를 사용한 중앙 정렬 */
                .curriculum-content p[style*="text-align: center"],
                .curriculum-content p[style*="text-align:center"] {
                  display: flex !important;
                  flex-direction: column !important;
                  align-items: center !important;
                  justify-content: center !important;
                }

                .curriculum-content img {
                  max-width: 100%;
                  height: auto;
                }
              `}
            </style>
            <div
              className="curriculum-content prose prose-lg prose-gray"
              style={{ maxWidth: '56rem', margin: '0 auto', padding: '0 2rem' }}
              dangerouslySetInnerHTML={{ __html: content.description }}
            />
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default CurriculumInfo;
